import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Trash2, Users, Copy, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/** Settings: four short sections. Rules, Questions, old Chores, reduction Categories and the Cash-outs tab were
 *  taken off the website on 5 Nov 2026 (Oscar OK'd the simpler layout); their data and routes are kept. Cash-outs are
 *  answered under Requests, tokens are given on each kid's page. */
export default function Family() {
  return (
    <div data-testid="family-page" className="max-w-4xl">
      <div className="mb-6">
        <h1 className="text-[32px] md:text-[38px] font-bold tracking-tight text-[#2C342A]">Settings</h1>
        <p className="text-sm text-[#707A6E]">For the whole family.</p>
      </div>
      <div className="space-y-8">
        <section data-testid="settings-family-members"><CoParentsPanel /></section>
        <Section title="Tokens"><TokensPanel /></Section>
        <Section title="If Yoda Watch is deleted"><DeletedPanel /></Section>
        <Section title="Your account"><AccountPanel /></Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section data-testid={`settings-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <h2 className="mb-3 text-[20px] font-bold text-[#2C342A]">{title}</h2>
      {children}
    </section>
  );
}

/** The two family-wide switches for a kid who deletes Yoda Watch (they used to sit on every kid's page). */
function DeletedPanel() {
  const [watch, setWatch] = useState(null);
  useEffect(() => { api.get("/family/device-watch").then((r) => setWatch(r.data)).catch(() => {}); }, []);
  async function flip(key) {
    const { data } = await api.put("/family/device-watch", { [key]: !watch[key] });
    setWatch(data);
  }
  if (!watch) return null;
  return (
    <div className="card-soft p-6" data-testid="keep-yoda-card">
      <p className="mb-3 text-sm text-[#707A6E]">For every kid. You get an email either way.</p>
      <label className="flex items-start gap-3 py-1.5 text-sm text-[#2C342A]">
        <input type="checkbox" checked={watch.deleted_loses_savings} onChange={() => flip("deleted_loses_savings")} className="mt-1" />
        <span><b>They lose their savings.</b> The unpaid wallet balance is held straight away and lost after 48 hours,
          unless Yoda is back on the phone by then. Their Wallet warns them about this.</span>
      </label>
      <label className="flex items-start gap-3 py-1.5 text-sm text-[#2C342A]">
        <input type="checkbox" checked={watch.deleted_locks_screens} onChange={() => flip("deleted_locks_screens")} className="mt-1" />
        <span><b>Their other screens lock</b> (iPad, PC) until Yoda is back on the phone.</span>
      </label>
    </div>
  );
}

function AccountPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="card-soft flex flex-wrap items-center gap-4 p-6" data-testid="account-card">
      <div className="min-w-0 flex-1 text-sm">
        <div className="font-semibold text-[#2C342A]">{user?.name || user?.email}</div>
        <div className="text-[#707A6E]">
          {user?.email} · {user?.email_verified === false ? "email not checked yet" : "email checked"}
        </div>
      </div>
      <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="settings-sign-out"
              className="btn-soft inline-flex items-center gap-1.5 px-4 py-2.5 text-sm">
        <LogOut size={16} /> Sign out
      </button>
    </div>
  );
}

/* ---------- CO-PARENTS ---------- */
function CoParentsPanel() {
  const [members, setMembers] = useState([]);
  const [link, setLink] = useState("");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = () => api.get("/family/members").then((r) => setMembers(r.data));
  useEffect(() => { load(); }, []);
  const invite = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/family/invite", email ? { email } : {});
      setLink(data.url || `${window.location.origin}/invite?token=${data.token}`);
      setSent(!!data.emailed);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(link); } catch (_) { /* ignore */ } };
  return (
    <div className="space-y-6" data-testid="coparents-panel">
      <div className="card-soft p-6">
        <div className="flex items-center gap-2 mb-3">
          <Users size={16} className="text-[#5C7A4F]" />
          <h3 className="font-heading text-lg font-semibold text-[#2C342A]">Family members</h3>
        </div>
        <div className="space-y-1">
          {members.map((m) => (
            <div key={m.id} className="flex items-center justify-between py-2 border-b border-[#E4E2DC] last:border-0" data-testid={`family-member-${m.id}`}>
              <div>
                <span className="font-medium text-[#2C342A]">{m.name || "(no name)"}</span>
                <span className="text-[#707A6E] text-sm ml-2">{m.email}</span>
              </div>
              {m.is_me && <span className="text-xs text-emerald-700 bg-emerald-500/10 px-2 py-0.5 rounded-full">you</span>}
            </div>
          ))}
        </div>
      </div>

      <div className="card-soft p-6">
        <h3 className="font-heading text-lg font-semibold text-[#2C342A] mb-2">Invite a co-parent</h3>
        <p className="text-[#707A6E] text-sm mb-4">They see the same kids and requests as you, and can do everything you can.</p>
        {!link ? (
          <div className="space-y-3">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="Their email (optional — will send an invite email)"
                   data-testid="invite-email-field"
                   className="w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3" />
            <button onClick={invite} disabled={busy} data-testid="invite-generate-btn"
                    className="inline-flex items-center gap-2 rounded-xl bg-[#5C7A4F] text-white px-5 py-3 font-medium hover:bg-[#4B6640] disabled:opacity-50">
              <Users size={16} /> {busy ? "Creating…" : (email ? "Send invite email" : "Generate invite link")}
            </button>
          </div>
        ) : (
          <div>
            {sent && (
              <div className="mb-3 text-sm bg-emerald-500/10 text-emerald-800 rounded-xl px-3 py-2" data-testid="invite-sent-msg">
                Invite emailed to <b>{email}</b>.
              </div>
            )}
            <div className="flex gap-2">
              <input readOnly value={link} className="flex-1 rounded-xl border border-[#E4E2DC] bg-[#F0EFEB] px-3 py-2 text-sm font-mono" data-testid="invite-link-display" />
              <button onClick={copy} className="rounded-xl bg-[#5C7A4F] text-white px-3 hover:bg-[#4B6640]" data-testid="invite-copy-btn"><Copy size={14} /></button>
            </div>
          </div>
        )}
        {err && <p className="text-sm text-[#C06E52] mt-3">{err}</p>}
      </div>
    </div>
  );
}

/* ---------- CASH-OUTS ---------- */
function TokensPanel() {
  const [types, setTypes] = useState([]);
  const [kids, setKids] = useState([]);
  const [wallets, setWallets] = useState({});
  const [form, setForm] = useState({ name: "", category: "", dollar_value: 1 });
  const [editing, setEditing] = useState(null); // { id, name, category, dollar_value }

  const load = async () => {
    const [t, k] = await Promise.all([api.get("/tokens"), api.get("/kids")]);
    setTypes(t.data); setKids(k.data);
    const w = {};
    for (const kid of k.data) {
      w[kid.id] = (await api.get(`/kids/${kid.id}/wallet`)).data;
    }
    setWallets(w);
  };
  useEffect(() => { load(); }, []);

  const addType = async (e) => { e.preventDefault(); await api.post("/tokens", form); setForm({ name: "", category: "", dollar_value: 1 }); load(); };
  const saveEdit = async () => {
    if (!editing) return;
    await api.patch(`/tokens/${editing.id}`, {
      name: editing.name, category: editing.category, dollar_value: parseFloat(editing.dollar_value) || 0,
    });
    setEditing(null); load();
  };
  const deleteType = async (t) => {
    if (!window.confirm(`Delete token "${t.name}"? Existing awards to kids stay; nothing new can be awarded.`)) return;
    await api.delete(`/tokens/${t.id}`); load();
  };

  return (
    <div className="space-y-8">
      <div className="card-soft p-6">
        <p className="mb-4 text-sm text-[#707A6E]">What kids can earn, and what each token is worth. Give tokens and pay out on each kid's page, under Wallet.</p>
        <form onSubmit={addType} className="grid md:grid-cols-4 gap-3 mb-4" data-testid="token-add-form">
          <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border border-[#E4E2DC] bg-white px-3 py-2" data-testid="token-name-input" />
          <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl border border-[#E4E2DC] bg-white px-3 py-2" data-testid="token-cat-input" />
          <input required type="number" step="0.25" min="0" placeholder="$ value" value={form.dollar_value}
                 onChange={(e) => setForm({ ...form, dollar_value: parseFloat(e.target.value) || 0 })}
                 className="rounded-xl border border-[#E4E2DC] bg-white px-3 py-2" data-testid="token-value-input" />
          <button className="rounded-xl bg-[#5C7A4F] text-white font-medium hover:bg-[#4B6640]" data-testid="token-add-btn">Add token</button>
        </form>
        <div className="space-y-1 text-sm">
          {types.map((t) => (
            <div key={t.id} className="border-b border-[#E4E2DC] py-2" data-testid={`token-type-${t.id}`}>
              {editing?.id === t.id ? (
                <div className="grid md:grid-cols-4 gap-2" data-testid={`token-edit-form-${t.id}`}>
                  <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                         className="rounded-lg border border-[#E4E2DC] bg-white px-2 py-1" data-testid={`token-edit-name-${t.id}`} />
                  <input value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                         placeholder="Category" className="rounded-lg border border-[#E4E2DC] bg-white px-2 py-1" data-testid={`token-edit-cat-${t.id}`} />
                  <input type="number" step="0.25" min="0" value={editing.dollar_value}
                         onChange={(e) => setEditing({ ...editing, dollar_value: e.target.value })}
                         className="rounded-lg border border-[#E4E2DC] bg-white px-2 py-1" data-testid={`token-edit-value-${t.id}`} />
                  <div className="flex gap-2">
                    <button onClick={saveEdit} className="flex-1 rounded-lg bg-[#5C7A4F] text-white hover:bg-[#4B6640] text-xs font-medium" data-testid={`token-edit-save-${t.id}`}>Save</button>
                    <button onClick={() => setEditing(null)} className="rounded-lg border border-[#E4E2DC] bg-white px-2 text-xs">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-[#2C342A]"><b>{t.name}</b> {t.category && <span className="text-[#707A6E]">· {t.category}</span>}</span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono">${t.dollar_value.toFixed(2)}</span>
                    <button onClick={() => setEditing({ id: t.id, name: t.name, category: t.category || "", dollar_value: t.dollar_value })}
                            className="text-[#5C7A4F] hover:text-[#4B6640] p-1" data-testid={`token-edit-btn-${t.id}`} title="Edit">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                    </button>
                    <button onClick={() => deleteType(t)}
                            className="text-[#C06E52] hover:text-[#A35A42] p-1" data-testid={`token-delete-btn-${t.id}`} title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {types.length === 0 && <p className="text-[#707A6E] text-sm">No token types yet.</p>}
        </div>
      </div>

      <div className="card-soft p-6">
        <h3 className="font-heading text-lg font-medium text-[#2C342A] mb-3">Kid balances</h3>
        {kids.map((k) => (
          <div key={k.id} className="flex items-center justify-between py-2 border-b border-[#E4E2DC] last:border-0" data-testid={`wallet-${k.id}`}>
            <span className="font-medium text-[#2C342A]">{k.name}</span>
            <span className="font-mono text-[#5C7A4F] font-semibold">${(wallets[k.id]?.balance ?? 0).toFixed(2)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
